<template>
  <FormLayout
    hide-side-portal
    :has-changes="true"
    :is-saving="isCreatingLocation || isBulkUpdatingLocationsTags"
    title="Add new location"
    @on-save="submit"
    @on-dismiss="dismiss"
  >
    <vs-alert v-if="errorMessage" data-test="error-alert" type="error"
      >{{ errorMessage }}
    </vs-alert>

    <vs-form ref="form">
      <OnTheFlyLocationRequiredFields
        :location-name="formData.location"
        :location-type-id="formData.locationTypeId"
        :location-owner-id="formData.ownerId"
        :location-private="formData.private"
        @location-name-input="handleFormDataChanged($event, 'name')"
        @location-type-input="handleFormDataChanged($event, 'location-type')"
        @location-owner-input="handleFormDataChanged($event, 'owner')"
        @location-private-input="handleFormDataChanged($event, 'private')"
      />

      <OnTheFlyLocationCoordinatesField
        ref="coordinatesField"
        :show-coordinates-field="showCoordinatesField"
        :latitude="formData.latitude"
        :is-in-map-view="isInMapView"
        :map-center-point="mapCenterPoint"
        :longitude="formData.longitude"
        @remove-coordinates="removeCoordinates"
        @on-minimap-moved="onMinimapMoved"
        @on-latitude-input="onLatitudeInput"
        @on-longitude-input="onLongitudeInput"
      />

      <OnTheFlyLocationOptionalFields
        :show-description-field="showDescriptionField"
        :show-parent-location-field="showParentLocationField"
        :show-status-field="showStatusField"
        :show-tags-field="showTagsField"
        :parent-id="formData.parentId"
        :description="formData.description"
        :color-id="formData.colorId"
        @remove-parent-location-field="removeParentLocation"
        @remove-description-field="removeDescription"
        @remove-status-field="removeStatus"
        @remove-tags-field="removeTags"
        @parent-location-input="handleFormDataChanged($event, 'parent-location')"
        @description-input="handleFormDataChanged($event, 'description')"
        @color-input="handleFormDataChanged($event, 'color')"
        @tags-input="handleFormDataChanged($event, 'tags')"
      />

      <OnTheFlyLocationOptionalFieldsMenu
        :show-description-field="showDescriptionField"
        :show-parent-location-field="showParentLocationField"
        :show-coordinates-field="showCoordinatesField"
        :show-status-field="showStatusField"
        :show-tags-field="showTagsField"
        @toggle-coordinates-field-on="toggleCoordinatesFieldOn"
        @toggle-parent-location-field-on="showParentLocationField = true"
        @toggle-description-field-on="showDescriptionField = true"
        @toggle-status-field-on="showStatusField = true"
        @toggle-tags-field-on="showTagsField = true"
      />
    </vs-form>
  </FormLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useCreateLocationMutation } from '@/api/account-data/manage-locations'
import { useFormStates } from '@/components/locations/on-the-fly-locations/form-states'
import { useMapView } from '@/components/locations/on-the-fly-locations/map-view'
import { useMinimapView } from '@/components/locations/on-the-fly-locations/minimap-view'
import LocationDTO from '@/models/location'
import VsAlert from '@/components/vision/VsAlert.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsForm from '@/components/vision/VsForm.vue'
import OnTheFlyLocationRequiredFields from '@/components/locations/on-the-fly-locations/OnTheFlyLocationRequiredFields.vue'
import OnTheFlyLocationOptionalFieldsMenu from '@/components/locations/on-the-fly-locations/OnTheFlyLocationOptionalFieldsMenu.vue'
import OnTheFlyLocationCoordinatesField from '@/components/locations/on-the-fly-locations/OnTheFlyLocationCoordinatesField.vue'
import OnTheFlyLocationOptionalFields from '@/components/locations/on-the-fly-locations/OnTheFlyLocationOptionalFields.vue'
import NewTag from '@/models/new_tag'
import { BulkUpdateLocationsTagsOperation, useBulkUpdateLocationsTags } from '@/api/locations'
const props = defineProps<{
  defaultParentLocId?: number | null
}>()
const emit = defineEmits(['close'])

const form = ref()
const coordinatesField = ref()
const errorMessage = ref<string>()

const { isLoading: isCreatingLocation, mutateAsync: createLocation } = useCreateLocationMutation()
const { mutateAsync: bulkUpdateLocationsTags, isLoading: isBulkUpdatingLocationsTags } =
  useBulkUpdateLocationsTags()
const {
  formData,
  mapCenterPoint,
  isInMapView,
  showCoordinatesField,
  removeCoordinates,
  showParentLocationField,
  removeParentLocation,
  showDescriptionField,
  removeDescription,
  showStatusField,
  removeStatus,
  showTagsField,
  removeTags,
} = useFormStates(() => props.defaultParentLocId)

const { map, insertLocationIntoMapModifiedLayer, syncLocationDetailsToMapMarker } = useMapView(
  formData,
  isInMapView,
)

const { onMinimapMoved } = useMinimapView(formData, isInMapView, mapCenterPoint)

// Methods
const dismiss = () => {
  emit('close', null)
}
const submit = async () => {
  if (!form.value?.isValid()) return

  if (
    formData.ownerId &&
    formData.location &&
    formData.locationTypeId != null &&
    (formData.accessBusinessUnitIds || []).length > 0
  ) {
    const response = await createLocation({
      ...(formData as Partial<LocationDTO>),
    })
    if (response.data) {
      if (formData.tags && formData.tags.length > 0) {
        await bulkUpdateLocationsTags({
          locationIds: [response.data],
          tags: formData.tags.map((tag) => NewTag.toObject(tag).toDisplayString),
          operation: BulkUpdateLocationsTagsOperation.OVERWRITE,
        })
      }
      if (isInMapView.value) {
        await insertLocationIntoMapModifiedLayer(response.data)
      }
      emit('close', response.data)
    }
  } else {
    errorMessage.value = `Please fill all required fields`
  }
}
const handleFormDataChanged = (
  e: string | number | number[] | NewTag[] | null | undefined,
  fieldChanged:
    | 'name'
    | 'location-type'
    | 'owner'
    | 'parent-location'
    | 'description'
    | 'color'
    | 'private'
    | 'tags',
) => {
  if (fieldChanged === 'name') {
    formData.location = e?.toString() ?? null
  } else if (fieldChanged === 'location-type') {
    if (e === null || e === undefined) {
      formData.locationTypeId = null
    } else {
      formData.locationTypeId = typeof e === 'number' ? e : parseInt(e as string)
    }
  } else if (fieldChanged === 'owner') {
    formData.ownerId = e?.toString() ?? null
  } else if (fieldChanged === 'parent-location') {
    if (e === null || e === undefined) {
      formData.parentId = null
    } else {
      formData.parentId = typeof e === 'number' ? e : parseInt(e as string)
    }
  } else if (fieldChanged === 'description') {
    formData.description = e?.toString() ?? null
  } else if (fieldChanged === 'color') {
    if (e === null || e === undefined) {
      formData.colorId = null
    } else {
      formData.colorId = typeof e === 'number' ? e : parseInt(e as string)
    }
  } else if (fieldChanged === 'private') {
    formData.private = !!e
  } else if (fieldChanged === 'tags') {
    formData.tags = e as NewTag[]
  }
  if (isInMapView.value) {
    syncLocationDetailsToMapMarker()
  }
}
const onLatitudeInput = (e: string) => {
  const parsedData = parseFloat(parseFloat(e).toFixed(8))
  if (!isNaN(parsedData)) {
    formData.latitude = parsedData
    if (!isInMapView.value) {
      coordinatesField.value.jumpMinimapTo({
        latitude: formData.latitude ?? 0,
        longitude: formData.longitude ?? 0,
      })
    }
  }
}
const onLongitudeInput = (e: string) => {
  const parsedData = parseFloat(parseFloat(e).toFixed(8))
  if (!isNaN(parsedData)) {
    formData.longitude = parsedData
    if (!isInMapView.value) {
      coordinatesField.value.jumpMinimapTo({
        latitude: formData.latitude ?? 0,
        longitude: formData.longitude ?? 0,
      })
    }
  }
}

const toggleCoordinatesFieldOn = () => {
  if (isInMapView.value && map.value) {
    const { lat, lng } = map.value.getCenter()
    formData.latitude = lat
    formData.longitude = lng
  } else {
    formData.latitude = mapCenterPoint.value?.latitude ?? 0
    formData.longitude = mapCenterPoint.value?.longitude ?? 0
  }
  showCoordinatesField.value = true
}
</script>
